import { spawnSync } from "node:child_process";
import { SSH_OPTIONS } from "./channel.js";
import { asPaneId } from "./ids.js";
import {
  currentJumpCommand,
  shellQuote as quoteCommand,
  renderJumpCommand,
  suggestedJumpCommand,
  supportsServer,
} from "./jump-command.js";
import { type Mux, tmux } from "./mux.js";
import type { Store } from "./store.js";
import type { PeerRecord } from "./types.js";
import type { PaneView } from "./view.js";

/**
 * The last segment of a slash-separated tmux session name.
 *
 * Session names are conventionally paths -- `tms` and friends name a session
 * after the directory it was opened in -- so the last segment identifies the
 * work: `hacking/murmur` is about `murmur`.
 *
 * SESSION names only. A window name that reaches the label is one a human chose
 * (see `chosenWindowName`) and shortening it would be presumptuous; a session
 * name is the last-resort fallback and almost never chosen by hand.
 *
 * Not `path.basename`: a session name only looks like a path. Splitting on `/`
 * cannot start resolving `..` or behaving differently per platform.
 *
 * Degenerate inputs keep the original rather than returning "": a session called
 * `/` has no last segment, and an empty name column is worse than an odd one.
 */
export function sessionLeaf(session: string): string {
  const leaf = session.split("/").filter(Boolean).at(-1);
  return leaf ?? session;
}

/**
 * The most specific human-readable name a pane's agent has, never a tmux id.
 *
 * Four sources, most to least specific: mu's agent name, pi's session name, the
 * tmux window name, the tmux session name. All are recorded by the node owning
 * the pane, so a local and a remote row read the same -- a reader cannot resolve
 * a remote window id against its own tmux.
 *
 * The session name is shortened to its last segment, and that last rung is
 * reached far more often than it looks: `agent_name` is mu-only, `window_name`
 * is null whenever tmux is auto-renaming (its default), and `pi_session` is
 * null for the whole life of an unnamed session, because pi's auto-namer runs
 * at CLOSE. So the common row for a hand-started pi printed `hacking/murmur` --
 * a path where a name belongs.
 *
 * The full name is not lost: the picker's stream column shows it, and shows it
 * BECAUSE of this shortening, since that column blanks when it would repeat the
 * name. Shortened, the row reads `murmur` + `hacking/murmur` -- same width,
 * strictly more information.
 *
 * Falls back to the window id only when a node recorded no names at all, which
 * means a non-tmux harness.
 */
export function agentLabel(agent: PaneView): string {
  const name =
    agent.agent_name ??
    agent.pi_session ??
    agent.window_name ??
    (agent.session_name === null ? null : sessionLeaf(agent.session_name));
  return terminalText(name ?? agent.window);
}

/**
 * Where the pane lives, for the second column. Names only -- the ids are what
 * jumps, not what a human reads.
 */
export function agentLocation(agent: PaneView): string {
  const session = agent.session_name ?? agent.session;
  const window = agent.window_name ?? agent.window;
  return terminalText(session === window ? session : `${session}:${window}`);
}

export function terminalText(value: string): string {
  return [...value]
    .map((character) => {
      const code = character.charCodeAt(0);
      return code < 0x20 || code === 0x7f || (code >= 0x80 && code <= 0x9f) ? "�" : character;
    })
    .join("");
}

export function shellQuote(value: string): string {
  return `'${value.replaceAll("'", `'\\''`)}'`;
}

/**
 * The local session name that wraps a remote attach.
 *
 * The trailing `~` marks it as murmur's, for a human reading a session list and
 * for the `#{m:*~,...}` match in the suggested escape-hatch binding.
 *
 * The invariant is: no character tmux's target grammar reserves, ANYWHERE in
 * the name -- not merely no leading sigil.
 *
 * Leading `@`, `$`, `%` and `=` are parsed as a window, session or pane id, so a
 * session named `@bubba` -- what the old per-host window was called -- cannot be
 * addressed at all: every `-t @bubba` fails with `can't find window`. Window
 * names were never targets, so the old name was safe; session names are.
 *
 * A colon is worse, and stripping only the prefix missed it. `:` separates
 * session from window in a target, so a peer named `a:b` produced
 * `switch-client -t '=a:b~'` -> `can't find session: a`, AFTER `newSession` had
 * already created the session. The jump then returned `attach_failed` and left
 * an orphan behind with `status` on and the local prefix live -- which
 * `sessionNamed` matched on the next attempt, so every later jump to that host
 * took the reuse branch and failed identically. Permanent, self-reinforcing, and
 * recoverable only by killing the session by hand. `.` is reserved the same way
 * and goes with it.
 *
 * Peer names come from `murmur peer add <name>` and default to the ssh target,
 * so this needs only a bracketed IPv6 literal, an rsync-style `host:path` habit,
 * or a name someone typed. Whitespace is replaced too: it survives quoting but
 * reads badly in a session list.
 */
/**
 * The peer that reaches a pane's host, and the ssh target to use for it.
 *
 * One boundary for a POLICY, not a convenience: "match on host_id, prefer
 * `target`, fall back to `name`". It was written out twice -- here and in
 * `glance` -- so peer identity gaining a second key would have needed both sites
 * changed with nothing saying so.
 *
 * Returns the row and the target together, so callers narrow on the PEER rather
 * than on the target. Narrowing on the target was what left both sites carrying
 * defensive `peer?.name` and `peer?.last_error` where `peer` was provably
 * defined.
 */
export function peerForHost(
  store: Store,
  hostId: string,
): { peer: PeerRecord; target: string } | null {
  const peer = store.peers().find((candidate) => candidate.host_id === hostId);
  if (!peer) return null;
  const target = peer.target ?? peer.name;
  return target ? { peer, target } : null;
}

export function remoteSessionName(peerName: string, hostId?: string): string {
  const label = peerName.replace(/^[@$%=]+/, "").replaceAll(/[:.\s]+/g, "-");
  // The sanitised label is NOT injective -- `a:b` and `a.b` both become `a-b`,
  // and `@foo` becomes `foo`. Reuse is matched on this name, so two peers
  // colliding here meant a jump to the second finding the FIRST one's wrapper,
  // taking the reuse branch, and attaching the operator to the wrong machine
  // while reporting ok. That is the failure the pane retarget already fixed one
  // level down, reappearing at the level of the host.
  //
  // The host id disambiguates because it is what actually identifies the
  // destination; the name is a label a human typed. Hashed rather than sliced:
  // a host id is a uuid in production but nothing enforces that, and slicing
  // hex characters out of an arbitrary string reintroduces the collision it is
  // here to prevent (`remote-host` yields `ee`). Six base-36 characters of a
  // cheap hash keep the session list readable and separate any two ids.
  //
  // Optional so a caller without a host id still gets the sanitised form.
  if (!hostId) return `${label}~`;
  let hash = 0;
  for (const character of hostId) hash = (Math.imul(hash, 31) + character.charCodeAt(0)) | 0;
  return `${label}-${Math.abs(hash).toString(36).padStart(6, "0").slice(0, 6)}~`;
}

/**
 * The process calls jump makes outside tmux: the remote probe and the configured
 * interactive command. Injectable so the jump decision
 * table is testable without an ssh binary or a live peer -- without this seam,
 * replacing `jumpToAgent`'s body with `return { ok: true }` kept every jump
 * test green.
 */
export type Runner = (
  file: string,
  args: string[],
  inherit?: boolean,
) => { status: number | null; stdout: string; failed: boolean };

/**
 * Bounds the PROBE, and only the probe.
 *
 * A probe runs on the picker's interactive path, so it must not hang the list;
 * ten seconds is far above a warm ssh and still finite for a sleeping laptop.
 */
export const PROBE_TIMEOUT_MS = 10_000;

/**
 * The real runner, parameterised by the probe's deadline.
 *
 * The deadline is an argument rather than a module constant so the timeout
 * POSTURE can be asserted in milliseconds. It used to be baked in, which left
 * only one way to prove the interactive path is unbounded: outlive the real 10s
 * with a `sleep 12`, and pay 22 seconds of wall clock across two tests on every
 * run. The posture is the claim; the size of the number never was.
 *
 * Same lesson as the two-postures comment below, one level up: a constant that
 * only one caller may choose is a seam missing, not a value.
 */
export function makeSpawnRunner(probeTimeoutMs: number = PROBE_TIMEOUT_MS): Runner {
  return (file, args, inherit = false) => {
    const result = spawnSync(file, args, {
      encoding: "utf8",
      // `inherit` means the user is SITTING IN this process -- the outside-tmux
      // ssh attach -- and a session a human is working in must have no deadline.
      // spawnSync's timeout is a hard kill, so the shared 10s SIGTERMed the ssh
      // ten seconds into a working remote pane, and because that leaves
      // `status: null` with ETIMEDOUT, `failed` went true and murmur reported
      // "ssh attach failed" with exit 1 for a session that was fine.
      //
      // Two callers, two postures: the same lesson as SSH_OPTIONS in channel.ts,
      // where one posture reused across two purposes was also the bug.
      ...(inherit ? { stdio: "inherit" as const } : { timeout: probeTimeoutMs }),
    });
    return {
      status: result.status,
      stdout: result.stdout ?? "",
      // spawnSync reports a failure to start the child in `error` and leaves
      // status null. Collapsing both keeps the decision table below one question.
      failed: result.error !== undefined,
    };
  };
}

/**
 * Exported for its TIMEOUT POSTURE only, which no fake can assert: every jump
 * test injects a `Runner`, so the real one's options object was the one thing
 * with no coverage -- and a hard kill on the interactive path is exactly the
 * kind of bug that hides there.
 */
export const spawnRunner: Runner = makeSpawnRunner();

export type JumpResult =
  | { ok: true }
  | {
      ok: false;
      // Local to this process: the picker prints `message` and nothing else, and
      // no reason code here is ever stored or published in a snapshot.
      reason:
        | "no_peer"
        | "unreachable"
        | "no_tmux"
        | "pane_gone"
        | "unsupported_server"
        | "attach_failed";
      message: string;
    };

/**
 * Jump to a pane, wherever it lives.
 *
 * NEVER MUTATES STATE ON FAILURE. A failure is a report: a reason and a message,
 * nothing written. The next collect reconciles either way, and only the owning
 * node can author facts about its own panes.
 */
export function jumpToAgent(
  store: Store,
  agent: PaneView,
  mux: Mux = tmux,
  run: Runner = spawnRunner,
): JumpResult {
  if (agent.local) {
    // The PANE decides. This once asked about the WINDOW, which a live pane
    // routinely outlives: after `move-pane -s %0 -t @1`, list-panes still has
    // %0 and list-windows no longer has @0, so healthy agents read as gone.
    const panes = mux.livePanes(agent.server);
    if (panes && !panes.has(agent.pane)) {
      return {
        ok: false,
        reason: "pane_gone",
        message: `${agentLabel(agent)} is gone -- its pane no longer exists.`,
      };
    }
    // Reported, not assumed. A failed attach is the local twin of the remote
    // symptom: the picker closes, nothing moves, nothing says why.
    //
    // Keyed on the PANE, the same thing liveness was just asked about, so the
    // check above and the action here cannot disagree about what is being
    // jumped to. Passing the recorded window instead meant a pane that had
    // moved passed the liveness check and then failed to attach.
    if (!mux.attach(agent.pane, agent.server)) {
      return {
        ok: false,
        reason: "attach_failed",
        message: `could not attach to ${agentLabel(agent)} (tmux switch-client failed).`,
      };
    }
    return { ok: true };
  }
  const resolved = peerForHost(store, agent.host_id);
  const peer = resolved?.peer;
  const target = resolved?.target;
  if (!peer || !target) {
    return {
      ok: false,
      reason: "no_peer",
      message: `No peer configured for host ${agent.host_id.slice(0, 8)}. Try: murmur peer add <target>`,
    };
  }

  const template = currentJumpCommand(peer.jump_command, target);
  if (!supportsServer(template, agent.server)) {
    return {
      ok: false,
      reason: "unsupported_server",
      message:
        `murmur: ${peer.name}'s jump command uses {pane}, which cannot identify the tmux server holding ${agentLabel(agent)} (${agent.pane})\n\n` +
        `update it:\n  murmur peer set ${/^[\w.-]+$/.test(peer.name) ? peer.name : quoteCommand(peer.name)} --jump-command ${quoteCommand(suggestedJumpCommand(peer.jump_command, target))}`,
    };
  }

  // Confirm the pane exists before opening a window to attach to it, and ask
  // about PANES: a recorded window id goes stale every time the pane moves.
  // Without this the attach fails inside a new tmux window that closes
  // instantly, indistinguishable from "enter did nothing" -- the symptom that
  // sent us hunting a quoting bug that did not exist.
  //
  // The format string is quoted because ssh takes no argv: it joins its
  // arguments and hands the string to a remote shell, which mangles a bare
  // `#{pane_id}` into tmux answering `-F expects an argument` -- which looked
  // exactly like an unreachable host.
  //
  // Shares the collector's SSH_OPTIONS rather than passing BatchMode alone.
  // Without ControlPath the probe misses the warm master socket the collector
  // rides; without ConnectTimeout it inherits the kernel's 75s dial, so a
  // sleeping laptop froze the picker for ten seconds before admitting it.
  //
  // The probe also ARMS the jump marker when there is a local session to come
  // back to, so `murmur dash --goto` on the remote machine detaches instead of
  // switching. Appended to the probe rather than sent as its own ssh: a second
  // round trip would double the jump's latency, and arming it after the attach
  // is impossible -- the hook has to exist before the client it marks. Appended
  // rather than prepended so the probe's pane list stays the readable output.
  //
  // `process.env.TMUX` gates it because the outside-tmux path below has no
  // wrapper and no local client: a detach there would drop the operator out of
  // their own shell's session for nothing.
  //
  // `|| true`, because the remote shell's exit status is its LAST command's: a
  // bare `;` would report the ARM's status and discard the probe's. The arm is
  // best effort -- an unmarked jump still lands on the agent, it only leaves
  // PREFIX G switching to the remote dash instead of coming home -- while the
  // probe's status is what every branch below reads. Without this, a remote
  // tmux too old for the indexed hook made every jump to that host report
  // "has no tmux server running" about a host that had just listed its panes.
  const tmuxPrefix =
    agent.server.kind === "default"
      ? "tmux"
      : `tmux ${agent.server.kind === "label" ? "-L" : "-S"} ${shellQuote(agent.server.value)}`;
  const arm = process.env.TMUX ? ` ; { ${tmuxPrefix} ${mux.armJumpMarkerCommand()} || true; }` : "";
  const probe = run("ssh", [
    ...SSH_OPTIONS,
    target,
    `${tmuxPrefix} list-panes -a -F ${shellQuote("#{pane_id}")}${arm}`,
  ]);
  if (probe.status !== 0) {
    // 255 is ssh's own failure code; anything else came from the remote command.
    // Conflating them was wrong in the common case: on a warm socket the host
    // answers instantly and it is tmux that is gone, so "unreachable" sent you
    // looking at the network for a problem that was not there.
    const sshFailed = probe.status === 255 || probe.failed;
    if (sshFailed) {
      // Nothing recorded: we learned only that we could not ask. Its agents may
      // be alive behind a cold socket, so deleting them would be guessing.
      return {
        ok: false,
        reason: "unreachable",
        message: `cannot reach ${target} over ssh. Nothing here ever prompts for auth, so check the host is awake and reachable, or connect once by hand to see the real error.`,
      };
    }

    // ssh worked, tmux did not: a real fact about the host, still nothing
    // deleted. The peer's own next snapshot removes its panes, because only
    // that node may author about them.
    return {
      ok: false,
      reason: "no_tmux",
      message: `${target} has no tmux server running, so its agents are gone. They will disappear on the next collect.`,
    };
  }
  const remotePanes = new Set(probe.stdout.split("\n").filter(Boolean).map(asPaneId));
  if (!remotePanes.has(agent.pane)) {
    return {
      ok: false,
      reason: "pane_gone",
      message: `${agentLabel(agent)} is gone -- ${target} no longer has that pane.`,
    };
  }

  // The PANE, which the probe immediately above just proved is alive on that
  // host. Addressing the window instead was the remote twin of the local attach
  // defect and failed the same two ways: you landed on whichever pane that
  // window last had active (a split holding the agent and a shell drops you on
  // the shell), and a pane that moved windows since the peer's last export
  // attached to a stale `@id` -- "can't find window: @99" -- while the pane
  // itself was alive and had just been confirmed.
  //
  // Establishing the pane and then addressing the window is precisely what
  // "only a pane may decide whether an agent exists" exists to prevent, applied
  // one level short of the action. `tmux attach -t %pane` resolves session,
  // window and pane together, verified against a real tmux server.
  const attach = renderJumpCommand(template, agent);

  // Hand the ssh to tmux as its own detached SESSION rather than running it
  // here: `murmur pick` is usually a display-popup, and a popup is modal, so an
  // ssh started inside it dies the moment the picker exits and the remote pane
  // flashed and vanished.
  //
  // A session, not a window, because session options are per-session and that is
  // what makes the nesting stop being felt:
  //
  //   status off   -- the remote's own bar is the only one on screen.
  //   prefix None  -- ^b reaches the remote directly. No ^b b to learn.
  //
  // Both would be global on a window and would break every local session. The
  // cost is that the local server is unreachable from inside the wrapper; the
  // README documents a root-table key that detaches out.
  if (process.env.TMUX) {
    // Read BEFORE the wrapper exists, or the wrapper itself is recorded as the
    // place to come back to and the return is a no-op.
    const client = mux.clientName();
    const origin = mux.currentTarget();

    // Named after the peer as configured, matching the picker's host column: a
    // self-reported display_name can be a container id, unrecognisable in a
    // session list.
    const name = remoteSessionName(peer.name, agent.host_id);

    // Reuse this host's wrapper rather than stacking one per jump -- three jumps
    // to bubba left three identical windows. Matched on name, the only handle
    // available: the ssh is opaque and a remote session id is not a local
    // address.
    if (mux.sessionNamed(name)) {
      // RETARGET the remote client first. Reuse is matched on the host's name
      // alone, so without this the wrapper still shows whichever pane the last
      // jump left it on: jump to agent A on a host, come back, pick agent B on
      // the same host, and you land on A while the call reports `ok: true`. A
      // wrong destination claiming success is the failure this file's own
      // "a silently failed jump looked exactly like enter did nothing" comment
      // exists to prevent, and the local attach had the identical bug.
      //
      // One extra round trip, on the reuse path only, over the socket the
      // wrapper's own ssh already warmed. A failure here is not fatal: the
      // switch below still lands the operator on the right HOST, which is
      // better than refusing to move, so this reports rather than aborts.
      // The wrapper's ssh process and remote tmux client stay alive while the
      // local client is elsewhere, so reuse retargets that existing remote
      // client. No remote client-attached event occurs here; arming another
      // one-shot hook would only leave a trap for the next unrelated login.
      const retarget = run("ssh", [
        ...SSH_OPTIONS,
        target,
        `${tmuxPrefix} switch-client -t ${shellQuote(shellQuote(agent.pane))}`,
      ]);
      if (!mux.switchClient(client, name)) {
        return {
          ok: false,
          reason: "attach_failed",
          message: `could not switch to the existing ${name} session.`,
        };
      }
      // A failed retarget is NOT reported. `JumpResult`'s success case carries
      // no message, and widening it for a warning would make every caller
      // handle a third state for a case that leaves the operator on the right
      // host looking at the wrong pane -- recoverable by jumping again. The
      // limitation worth knowing is above; if this needs surfacing, the honest
      // change is a partial-success variant, not a message smuggled into `ok`.
      void retarget;
      return { ok: true };
    }

    // The peer's opaque command template owns its shell quoting. The default
    // retains both layers: one for this local shell and one for ssh's remote
    // shell, so a tmux target containing `$` cannot expand on either side.

    // The return home, inside the wrapper's own command: when the attach exits
    // -- inner detach, remote session killed, ssh dropped -- this runs, then the
    // wrapper has no command left and tmux destroys it.
    //
    // Explicit rather than trusting detach-on-destroy, whose `previous` picked a
    // stray unrelated session in testing rather than the one the jump started
    // from. Still set below, as the fallback for when this cannot run (SIGKILL).
    const restore = origin
      ? `; tmux switch-client ${client ? `-c ${shellQuote(client)} ` : ""}-t ${shellQuote(`=${origin}`)}`
      : "";

    if (!mux.newSession(name, `${attach}${restore}`)) {
      return {
        ok: false,
        reason: "attach_failed",
        message: `could not open a session to attach to ${target}.`,
      };
    }

    mux.setSessionOption(name, "status", "off");
    mux.setSessionOption(name, "prefix", "None");
    mux.setSessionOption(name, "detach-on-destroy", "previous");

    return mux.switchClient(client, name)
      ? { ok: true }
      : {
          ok: false,
          reason: "attach_failed",
          message: `attached to ${target} in session ${name}, but could not switch to it.`,
        };
  }

  // Outside tmux there is no popup to escape, so run it directly. stdio is
  // inherited, which both hands the terminal to the remote session and -- see
  // `spawnRunner` -- is what waives the probe's timeout, so this blocks for as
  // long as the user stays rather than being killed ten seconds in.
  //
  // None of the wrapper machinery above applies, and must not: there is no local
  // client to switch, nothing to return to but the invoking shell, and no local
  // status bar or prefix to suppress -- this path is already full-screen and
  // prefix-clean, since the whole problem is an artifact of being inside tmux.
  // A local session here would attach a client to a server the user never asked
  // for and leave them in tmux on exit rather than at their prompt.
  //
  // The tradeoff, no reuse of an existing attach, is correct rather than
  // missing: there is no local server holding one.
  const attached = run("sh", ["-c", attach], true);
  return attached.status === 0 && !attached.failed
    ? { ok: true }
    : {
        ok: false,
        reason: "attach_failed",
        message: `attach to ${target} failed.`,
      };
}
